// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.CrossFilterSettingAction.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * @module CrossFilterSettingAction popup component
 */

import m from 'mobx';
import { withHandlers, withState, mapProps, compose, withPropsOnChange } from 'recompose';
import {
  get,
  getOr,
  includes,
  filter,
  find,
  flow,
  forEach,
  without,
  isEmpty,
  map,
  merge,
  curry,
  difference
} from 'lodash/fp';

/**
 * Handle when select select all check box
 * @param {Object} props  Components props
 * @param {Object} e      React event
 */
const onSelectAll = (props: Object, e: Event) => {
  let status = get('target.checked', e);

  if (status) {
    props.updateSelectedReportParts(props.allReportParts);
  } else {
    props.updateSelectedReportParts([]);
  }
};

/**
 * On clossing cross filter setting popup
 * @param {Object} props component property
 * @param {function} callBack onClosing callback
 * @param {string} type accpet or cancel
 */
const onClosing = (props: Object, callBack: () => void, type: string) => {
  if (type === 'accept') {
    let reportParts = getOr([], 'store.reportBody.activeReportParts', props);
    let reportBody = getOr({}, 'store.reportBody', props);
    const originCrossFilteringReportParts = getOr([], 'store.reportBody.crossFilteringReportParts', props);
    const selectedReportParts = getOr([], 'selectedReportParts', props);

    const changedReport = difference(selectedReportParts, originCrossFilteringReportParts);
    if (selectedReportParts.length !== originCrossFilteringReportParts.length || changedReport.length > 0) {
      // reset all drill of all active repports.
      reportBody.removeAllPills();

      flow(
        filter(id => !includes(id, selectedReportParts)),
        forEach(
          m.action(id => {
            const reportPart = find({ id: id }, reportParts);
            reportPart.reportPartContent.resetDrilldownInfo && reportPart.reportPartContent.resetDrilldownInfo();
            props.store.needUpdatePreview(reportPart, null, null, null, null, true);
          })
        )
      )(reportBody.crossFilteringReportParts);

      reportParts.forEach(reportPart => {
        let crossFilteringValue = includes(reportPart.id, selectedReportParts);
        reportPart.setCrossFiltering(crossFilteringValue);
      });

      props.store.loadReportPartDataByCrossFiltering();
    }

    props.onAccept();
  } else {
    props.onReject();
  }
};

/**
 * On select report part
 * @param {Object} props
 * @param {Object} selectReportPart
 * @param {Object} e
 */
const onSelectReportParts = (props: Object, selectReportPart: Object, e: Event) => {
  let status = get('target.checked', e);
  let selectedReportParts = props.selectedReportParts;
  if (status) {
    selectedReportParts.push(selectReportPart.value);
  } else {
    selectedReportParts = without([selectReportPart.value], selectedReportParts);
  }
  props.updateSelectedReportParts(selectedReportParts);
};

/**
 * Props change when selected report part change
 * @param {*} props
 */
const handleStates = props => {
  let isSelectAll = false;
  let isIndeterminate = false;

  if (props.selectedReportParts.length === props.allReportParts.length) {
    isSelectAll = true;
  } else if (props.selectedReportParts.length !== 0) {
    isIndeterminate = true;
  }

  return {
    isIndeterminate: isIndeterminate,
    isSelectAll: isSelectAll
  };
};

const CrossFilterSettingAction = compose(
  withState('selectedReportParts', 'updateSelectedReportParts', props => {
    let selectedReportParts = flow(
      getOr([], 'store.reportBody.activeReportParts'),
      filter(({ headerTitle, reportPartContent }) => !isEmpty(headerTitle) && reportPartContent.isCrossFiltering),
      map(reportPart => reportPart.id)
    )(props);

    return selectedReportParts;
  }),
  mapProps(props => {
    let allReportParts = flow(
      getOr([], 'store.reportBody.activeReportParts'),
      filter(({ headerTitle }) => !isEmpty(headerTitle)),
      map(reportPart => reportPart.id)
    )(props);

    return merge(props, {
      allReportParts: allReportParts
    });
  }),
  withPropsOnChange(['selectedReportParts'], handleStates),
  withHandlers({
    onSelectAll: curry(onSelectAll),
    onClosing: curry(onClosing),
    onSelectReportParts: curry(onSelectReportParts)
  })
);

export default CrossFilterSettingAction;



// WEBPACK FOOTER //
// ./ui/modules/CrossFiltering/Izenda.CrossFilterSettingAction.js